import React, { useCallback, useEffect, useState } from 'react';
import {
    Button, Checkbox, Form, InputNumber, Modal, Popconfirm, Select, Space, Switch, Table, Tag, Tooltip, Typography, message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import apiClient from '../../../shared/services/apiClient';

const { Text } = Typography;

const api = {
    get: (url, params) => apiClient.get(`/api/admin${url}`, { params }).then((r) => r.data),
    post: (url, body) => apiClient.post(`/api/admin${url}`, body).then((r) => r.data),
};
const errText = (e) => e?.response?.data?.error || e?.error || e?.message || '操作失败';

const KIND = { dns: 'DNS', cdn: 'CDN', yeapmail: '邮箱', ssl: 'SSL', evm: '云服务器', host: '独享主机', host_shared: '虚拟主机', disk: '云盘' };
const POLICY = {
    free: { label: '继续免费', color: 'green' },
    coupon: { label: '优惠码价', color: 'blue' },
    list: { label: '原价', color: 'default' },
};
const CYCLE = { monthly: '月', quarterly: '季', semi_annually: '半年', annually: '年', yearly: '年', onetime: '一次性' };

/**
 * 域名注册赠品规则。scope=tld_pricing（后缀+注册商定价行，常规赠品）| promotion（特价活动，活动赠品）。
 * 改规则只影响之后的订单。
 */
const GiftRulesModal = ({ open, scope, refId, title, onClose, onChanged }) => {
    const [rows, setRows] = useState([]);
    const [opts, setOpts] = useState({ products: [], coupons: [] });
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null); // null | {} (new) | row
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();
    const policy = Form.useWatch('renewal_policy', form);
    const productId = Form.useWatch('gift_product_id', form);

    const refKey = scope === 'promotion' ? 'promotion_id' : 'tld_pricing_id';

    const load = useCallback(async () => {
        if (!refId) return;
        setLoading(true);
        try {
            const r = await api.get('/domain/gift-rules', { [refKey]: refId });
            if (r.success) setRows(Array.isArray(r.data) ? r.data : []);
            else message.error(r.error || '加载失败');
        } catch (e) {
            message.error(errText(e));
        } finally {
            setLoading(false);
        }
    }, [refId, refKey]);

    useEffect(() => {
        if (!open) return;
        load();
        api.get('/domain/gift-rules/options').then((r) => r.success && setOpts(r.data)).catch(() => {});
    }, [open, load]);

    const product = opts.products.find((p) => p.id === productId);
    const cycles = product?.cycles?.filter((c) => c.billing_cycle !== 'onetime') || [];

    const startEdit = (row) => {
        setEditing(row || {});
        form.setFieldsValue(row ? {
            gift_product_id: row.gift_product_id,
            billing_cycle: row.billing_cycle,
            renewal_policy: row.renewal_policy,
            coupon_id: row.coupon_id || undefined,
            operations: String(row.operations || 'register').split(','),
            min_years: row.min_years || 1,
            active: !!Number(row.active),
        } : { renewal_policy: 'free', billing_cycle: 'annually', operations: ['register'], min_years: 1, active: true });
    };

    const save = async () => {
        const v = await form.validateFields();
        setSaving(true);
        try {
            const r = await api.post('/domain/gift-rules/save', {
                ...v, id: editing?.id, scope, [refKey]: refId,
            });
            if (r.success) {
                setEditing(null);
                load();
                onChanged && onChanged();
            } else message.error(r.error || '保存失败');
        } catch (e) {
            message.error(errText(e));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (row) => {
        try {
            const r = await api.post('/domain/gift-rules/delete', { id: row.id });
            if (r.success) {
                if (r.data?.disabled) message.info('已发放过，改为停用');
                load();
                onChanged && onChanged();
            } else message.error(r.error || '删除失败');
        } catch (e) { message.error(errText(e)); }
    };

    const columns = [
        {
            title: '赠品', key: 'p', render: (_, r) => (
                <Space size={4} wrap>
                    <Tag>{KIND[r.product_type_code] || r.product_type_code}</Tag>
                    <span>{r.product_name || `#${r.gift_product_id}`}</span>
                    {!Number(r.active) && <Tag>停用</Tag>}
                </Space>
            ),
        },
        {
            title: '续费', key: 'r', width: 150, render: (_, r) => (
                <Space size={2} direction="vertical">
                    <Tag color={POLICY[r.renewal_policy]?.color}>{POLICY[r.renewal_policy]?.label}</Tag>
                    {r.renewal_policy === 'coupon' && <Text type="secondary" style={{ fontSize: 12 }}>{r.coupon_code}</Text>}
                </Space>
            ),
        },
        {
            title: '条件', key: 'c', width: 120, render: (_, r) => (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {String(r.operations).split(',').map((o) => (o === 'transfer' ? '转入' : '注册')).join('/')}
                    {Number(r.min_years) > 1 ? ` ≥${r.min_years}年` : ''}
                </Text>
            ),
        },
        { title: '已发', dataIndex: 'granted', width: 60 },
        {
            title: '', key: 'a', width: 120, render: (_, r) => (
                <Space size={4}>
                    <Button size="small" onClick={() => startEdit(r)}>编辑</Button>
                    <Popconfirm title="删除该赠品？" onConfirm={() => remove(r)}><Button size="small" danger>删除</Button></Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Modal open={open} title={title || '注册赠品'} onCancel={() => { setEditing(null); onClose(); }} footer={null} width={720} destroyOnClose>
            {editing === null ? (
                <>
                    <Button type="primary" icon={<PlusOutlined />} style={{ marginBottom: 12 }} onClick={() => startEdit(null)}>添加赠品</Button>
                    <Table rowKey="id" size="small" loading={loading} dataSource={rows} columns={columns} pagination={false}
                        locale={{ emptyText: '暂无赠品' }} scroll={{ x: 600 }} />
                </>
            ) : (
                <Form form={form} layout="vertical">
                    <Form.Item name="gift_product_id" label="赠品套餐" rules={[{ required: true, message: '请选择' }]}>
                        <Select showSearch optionFilterProp="label" disabled={!!editing?.id}
                            options={opts.products.map((p) => ({ value: p.id, label: `${KIND[p.product_type_code] || p.product_type_code} · ${p.name}${p.is_free ? '（免费版）' : ''}` }))} />
                    </Form.Item>
                    {!product?.is_free && (
                        <Space size={16} wrap>
                            <Form.Item name="renewal_policy" label={<Tooltip title="域名续费时赠品怎么续">续费方式</Tooltip>} rules={[{ required: true }]}>
                                <Select style={{ width: 140 }} options={Object.entries(POLICY).map(([k, v]) => ({ value: k, label: v.label }))} />
                            </Form.Item>
                            <Form.Item name="billing_cycle" label={<Tooltip title="原价/优惠码续费按此周期价 × 周期数">计价周期</Tooltip>} rules={[{ required: true }]}>
                                <Select style={{ width: 160 }} options={cycles.map((c) => ({ value: c.billing_cycle, label: `${CYCLE[c.billing_cycle] || c.billing_cycle} · ${c.price} ${c.currency}` }))} />
                            </Form.Item>
                            {policy === 'coupon' && (
                                <Form.Item name="coupon_id" label="优惠码" rules={[{ required: true, message: '请选择' }]}>
                                    <Select style={{ width: 200 }} showSearch optionFilterProp="label"
                                        options={opts.coupons.map((c) => ({ value: c.id, label: `${c.code} · ${c.type === 'percent' ? `${Number(c.value)}%` : `${c.value} ${c.currency}`}` }))} />
                                </Form.Item>
                            )}
                        </Space>
                    )}
                    <Space size={16} wrap>
                        {scope !== 'promotion' && (
                            <Form.Item name="operations" label="适用">
                                <Checkbox.Group options={[{ value: 'register', label: '注册' }, { value: 'transfer', label: '转入' }]} />
                            </Form.Item>
                        )}
                        <Form.Item name="min_years" label="最少年数">
                            <InputNumber min={1} max={10} style={{ width: 90 }} />
                        </Form.Item>
                        <Form.Item name="active" label="启用" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Space>
                    <Space>
                        <Button type="primary" loading={saving} onClick={save}>保存</Button>
                        <Button onClick={() => setEditing(null)}>返回</Button>
                    </Space>
                </Form>
            )}
        </Modal>
    );
};

export default GiftRulesModal;
